import { useEffect, useState } from "react";
import {
  FaBalanceScale,
  FaBarcode,
  FaComment,
  FaMapMarkerAlt,
  FaShapes,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  InstallationPickerOption,
  InstallationPickerRow,
  InstallationPickerSheet,
} from "../installation/InstallationPickerSheet";
import PhotoCaptureCommentSheet from "./PhotoCaptureCommentSheet";
import PhotoCaptureReferenceSheet from "./PhotoCaptureReferenceSheet";
import PhotoCaptureWeightWheelPicker from "./PhotoCaptureWeightWheelPicker";
import { formatPhotoCaptureCommentSubtitle } from "./photoCaptureMobileUtils";

const WasteMetadataFields = ({
  selectedClient,
  onClientChange,
  selectedWasteCategory,
  onWasteCategoryChange,
  identificationReference = "",
  onIdentificationReferenceChange,
  weightTonnes,
  onWeightChange,
  commentText,
  onCommentChange,
  clientOptions,
  wasteCategoryOptions,
  disabled = false,
  onActiveFieldSheetChange,
  onFieldCommit,
  showIdentificationReference = true,
}) => {
  const { t } = useTranslation();
  const [originSheetOpen, setOriginSheetOpen] = useState(false);
  const [wasteCategorySheetOpen, setWasteCategorySheetOpen] = useState(false);
  const [referenceSheetOpen, setReferenceSheetOpen] = useState(false);
  const [weightSheetOpen, setWeightSheetOpen] = useState(false);
  const [commentSheetOpen, setCommentSheetOpen] = useState(false);
  const [originSheetDraft, setOriginSheetDraft] = useState("");
  const [wasteCategorySheetDraft, setWasteCategorySheetDraft] = useState("");

  useEffect(() => {
    onActiveFieldSheetChange?.(
      originSheetOpen ||
        wasteCategorySheetOpen ||
        referenceSheetOpen ||
        weightSheetOpen ||
        commentSheetOpen,
    );
  }, [
    onActiveFieldSheetChange,
    commentSheetOpen,
    originSheetOpen,
    referenceSheetOpen,
    wasteCategorySheetOpen,
    weightSheetOpen,
  ]);

  useEffect(() => {
    if (originSheetOpen) {
      setOriginSheetDraft(selectedClient);
    }
  }, [originSheetOpen, selectedClient]);

  useEffect(() => {
    if (wasteCategorySheetOpen) {
      setWasteCategorySheetDraft(selectedWasteCategory);
    }
  }, [wasteCategorySheetOpen, selectedWasteCategory]);

  const selectedOriginOption = clientOptions.find(
    (option) => option.value === selectedClient,
  );
  const selectedWasteCategoryOption = wasteCategoryOptions.find(
    (option) => option.value === selectedWasteCategory,
  );
  const placeholder = t("Please select");
  const commentEmptyPlaceholder = t("mobile_photo_comment_empty");
  const referenceSubtitle = identificationReference?.trim()
    ? identificationReference.trim()
    : placeholder;
  const weightSubtitle = weightTonnes?.trim()
    ? `${weightTonnes} ${t("tons")}`
    : placeholder;
  const commentSubtitle = formatPhotoCaptureCommentSubtitle(
    commentText,
    commentEmptyPlaceholder,
  );
  const hasComment = Boolean(String(commentText ?? "").trim());

  const validateOriginSheet = () => {
    let nextReference = identificationReference;
    onClientChange(originSheetDraft);
    try {
      const parsed = JSON.parse(originSheetDraft);
      if (
        onIdentificationReferenceChange &&
        !identificationReference?.trim() &&
        (parsed.clientUniqueId || parsed.code)
      ) {
        nextReference = String(parsed.clientUniqueId || parsed.code);
        onIdentificationReferenceChange(nextReference);
      }
    } catch {
      // ignore
    }
    setOriginSheetOpen(false);
    onFieldCommit?.({
      selectedClient: originSheetDraft,
      identificationReference: nextReference,
    });
  };

  const validateWasteCategorySheet = () => {
    onWasteCategoryChange(wasteCategorySheetDraft);
    setWasteCategorySheetOpen(false);
    onFieldCommit?.({ selectedWasteCategory: wasteCategorySheetDraft });
  };

  const handleReferenceChange = (value) => {
    onIdentificationReferenceChange?.(value);
    onFieldCommit?.({ identificationReference: value });
  };

  const handleWeightChange = (value) => {
    onWeightChange(value);
    onFieldCommit?.({ weightTonnes: value });
  };

  const handleCommentChange = (value) => {
    onCommentChange(value);
    onFieldCommit?.({ commentText: value, commitComment: true });
  };

  return (
    <>
      <div className="installation-settings-card photo-capture-metadata-fields-card">
        <InstallationPickerRow
          icon={FaMapMarkerAlt}
          iconVariant="site"
          title={t("Waste origin")}
          subtitle={selectedOriginOption?.label}
          placeholder={placeholder}
          onClick={() => setOriginSheetOpen(true)}
          disabled={disabled}
        />
        <InstallationPickerRow
          icon={FaShapes}
          iconVariant="containers"
          title={t("Waste name")}
          subtitle={selectedWasteCategoryOption?.label}
          placeholder={placeholder}
          onClick={() => setWasteCategorySheetOpen(true)}
          disabled={disabled}
          divided
        />
        {showIdentificationReference && onIdentificationReferenceChange && (
          <InstallationPickerRow
            icon={FaBarcode}
            iconVariant="technical"
            title={t("Identification reference")}
            subtitle={referenceSubtitle}
            placeholder={placeholder}
            onClick={() => setReferenceSheetOpen(true)}
            disabled={disabled}
            divided
          />
        )}
        <InstallationPickerRow
          icon={FaBalanceScale}
          iconVariant="default"
          title={t("Weight")}
          subtitle={weightSubtitle}
          placeholder={placeholder}
          onClick={() => setWeightSheetOpen(true)}
          disabled={disabled}
          divided
        />
        <InstallationPickerRow
          icon={FaComment}
          iconVariant="default"
          title={t("Your comment")}
          subtitle={hasComment ? commentSubtitle : undefined}
          placeholder={commentEmptyPlaceholder}
          onClick={() => setCommentSheetOpen(true)}
          disabled={disabled}
          divided
        />
      </div>

      <InstallationPickerSheet
        open={originSheetOpen}
        title={t("Waste origin")}
        onSheetCancel={() => setOriginSheetOpen(false)}
        onSheetValidate={validateOriginSheet}
        sheetValidateDisabled={!originSheetDraft}
      >
        {clientOptions.map((option) => (
          <InstallationPickerOption
            key={option.key}
            label={option.label}
            selected={originSheetDraft === option.value}
            onSelect={() => setOriginSheetDraft(option.value)}
          />
        ))}
      </InstallationPickerSheet>

      <InstallationPickerSheet
        open={wasteCategorySheetOpen}
        title={t("Waste name")}
        onSheetCancel={() => setWasteCategorySheetOpen(false)}
        onSheetValidate={validateWasteCategorySheet}
        sheetValidateDisabled={!wasteCategorySheetDraft}
      >
        {wasteCategoryOptions.map((option) => (
          <InstallationPickerOption
            key={option.key}
            label={option.label}
            selected={wasteCategorySheetDraft === option.value}
            onSelect={() => setWasteCategorySheetDraft(option.value)}
          />
        ))}
      </InstallationPickerSheet>

      {showIdentificationReference && onIdentificationReferenceChange && (
        <PhotoCaptureReferenceSheet
          open={referenceSheetOpen}
          value={identificationReference}
          onChange={handleReferenceChange}
          onDismiss={() => setReferenceSheetOpen(false)}
          disabled={disabled}
        />
      )}

      <PhotoCaptureWeightWheelPicker
        open={weightSheetOpen}
        value={weightTonnes}
        onChange={handleWeightChange}
        onDismiss={() => setWeightSheetOpen(false)}
      />

      <PhotoCaptureCommentSheet
        open={commentSheetOpen}
        value={commentText}
        onChange={handleCommentChange}
        onDismiss={() => setCommentSheetOpen(false)}
        disabled={disabled}
      />
    </>
  );
};

export default WasteMetadataFields;
